import { PageHeading } from '@src/components/PageHeading/PageHeading';
import type { ReactNode } from 'react';
import { Container, type ContainerWidth } from '@src/components/Container/Container';
import { Bar } from '@src/components/Bar/Bar';
import { classes } from '@src/components/layout/space';
import { PageContent } from '@src/components/PageContent/PageContent';
import type { PanelProps } from '@src/components/Panel/Panel';
import { Panel } from '@src/components/Panel/Panel';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Settings.css';

export interface SettingsPageProps {
  children: ReactNode;
  testId?: string;
  embedded?: boolean;
  width?: ContainerWidth;
  layout?: 'continuous' | 'panels';
  className?: string;
}

/** Account and guest settings sit in a narrow column, like v1. */
export function SettingsPage({
  children,
  testId,
  embedded = false,
  width = 'medium',
  layout = 'continuous',
  className,
}: SettingsPageProps) {
  const content = (
    <div
      className={classes(
        'settings',
        className,
        embedded && 'settings--embedded',
        layout === 'panels' && 'settings--panels',
      )}
    >
      {children}
    </div>
  );
  return embedded ? (
    <Container width={width} testId={testId}>
      {content}
    </Container>
  ) : (
    <PageContent width={width} testId={testId}>
      {content}
    </PageContent>
  );
}

export interface SettingsPanelProps {
  children: ReactNode;
  /** Shared small edges pieces; defaults to the left corner and top mound. */
  edges?: PanelProps['edges'];
  decorated?: boolean;
}

export function SettingsPanel({ children, edges = { pieces: 3 }, decorated = true }: SettingsPanelProps) {
  return (
    <Panel padding="none" elevated wide edges={decorated ? edges : undefined} className="settings__panel">
      {children}
    </Panel>
  );
}

export interface SettingsHeaderProps {
  title: string;
  subtitle?: string;
  error?: string | null;
}

export function SettingsHeader({ title, subtitle, error }: SettingsHeaderProps) {
  return (
    <header className="settings__header">
      <PageHeading title={title} subtitle={subtitle} />
      {error ? (
        <Typography as="p" role="meta/error">
          {error}
        </Typography>
      ) : null}
    </header>
  );
}

export type SettingsSectionIconTone = 'accent' | 'support' | 'warning' | 'neutral' | 'danger';

export interface SettingsSectionProps {
  id?: string;
  title?: string;
  hint?: string;
  icon?: ReactNode;
  iconTone?: SettingsSectionIconTone;
  children: ReactNode;
  danger?: boolean;
  /** Neutral grey fill — account deletion and other low-emphasis warnings. */
  muted?: boolean;
  testId?: string;
}

export function SettingsSection({
  id,
  title,
  hint,
  icon,
  iconTone = 'neutral',
  children,
  danger = false,
  muted = false,
  testId,
}: SettingsSectionProps) {
  return (
    <section
      id={id}
      className={classes(
        'settings__section',
        !title && !hint && 'settings__section--fields-only',
        danger && 'settings__section--danger',
        muted && 'settings__section--muted',
        icon ? 'settings__section--icon' : undefined,
      )}
      {...testIdAttr(testId)}
    >
      {title || hint ? (
        <div className="settings__label">
          {icon ? (
            <span
              className={classes('settings__section-icon', `settings__section-icon--${iconTone}`)}
              aria-hidden="true"
            >
              {icon}
            </span>
          ) : null}
          <div className="settings__section-copy">
            <Typography as="h2" role="title/sub">
              {title}
            </Typography>
            {hint ? (
              <Typography as="p" role="meta/helper">
                {hint}
              </Typography>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="settings__fields">{children}</div>
    </section>
  );
}

export interface SettingsStepsProps {
  steps: string[];
  testId?: string;
}

/** Numbered how-to tiles — guest convert copy, one step per row. */
export function SettingsSteps({ steps, testId }: SettingsStepsProps) {
  return (
    <ol className="settings__steps" {...testIdAttr(testId)}>
      {steps.map((text, index) => (
        <li
          key={text}
          className="settings__step"
          {...testIdAttr(testIdFor(testId, `step-${index + 1}`))}
        >
          <span className="settings__step-mark" aria-hidden="true">
            {index + 1}
          </span>
          <Typography as="p" role="body/sm" className="settings__step-copy">
            {text}
          </Typography>
        </li>
      ))}
    </ol>
  );
}

export interface SettingsActionRowProps {
  title: string;
  icon?: ReactNode;
  hint?: string;
  danger?: boolean;
  children: ReactNode;
  testId?: string;
}

export function SettingsActionRow({
  title,
  icon,
  hint,
  danger = false,
  children,
  testId,
}: SettingsActionRowProps) {
  return (
    <div
      className={classes('settings__action', danger && 'settings__action--danger')}
      {...testIdAttr(testId)}
    >
      <Bar align="center" stack="narrow" gap={4} wide wrap={false}>
        <div className="settings__action-copy">
          <Typography
            as="p"
            role={danger && !icon ? 'body/sm' : 'label/control'}
            className="settings__action-title"
          >
            {icon ? <span className="settings__action-icon" aria-hidden="true">{icon}</span> : null}
            {title}
          </Typography>
          {hint ? (
            <Typography as="p" role="meta/helper" className="settings__action-hint">
              {hint}
            </Typography>
          ) : null}
        </div>
        {children}
      </Bar>
    </div>
  );
}
